import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Table from "../ogComponent/table";
import _ from "lodash";
import Spinner2 from "../ogComponent/Spinner2";
import { Box, Modal } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IconTrash, IconPencilMinus } from '@tabler/icons-react';
import "./employee.css";

export default function Employee() {
  const navigate = useNavigate();
  const [product, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("Active");
  const [selectedemployeeType, setSelectedemployeeType] = useState("Employee");
  const [searchQuery, setSearchQuery] = useState("");
  const [departments, setDepartments] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [empToDelete, setEmpToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEmployee, setSelectedEmployee] = useState({
    name: "All Departments",
    id: "",
  });

  // Access permissions array from Redux store
  const permissions = useSelector((state) => state.emp.permissions);

  const header = [
    { name: "Si No", label: "#", active: true },
    { name: "user_id", label: "User Code", active: true },
    { name: "name", label: "Name", active: true },
    { name: "job_position", label: "Job Title", active: true },
    { name: "contact_info", label: "Contact", active: true },
    { name: "Status", label: "Status", active: true },
    { name: "actions", label: "Actions", active: permissions.includes("deleteemployeeID") || permissions.includes("editemployeeID") },
  ];

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSearchParams({ page: newPage });
  };

  const handleOpenModal = (item) => {
    setEmpToDelete(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEmpToDelete(null);
  };

  const fetchDepartments = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/employee/departments/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchData = async (query = "", departmentId = "", status = "true", emp_type = 'Employee') => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/account/users-list/?name=${query}&department=${departmentId}&status=${status}&employee_type=${emp_type}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const transformedData = response.data.map((item, index) => ({
        id: item.id,
        "Si No": index + 1,
        name: (
          <div className="d-flex align-items-center gap-3">
            <img
              className="emp-cell-img"
              src={item.profile_photo || "/assets/user.png"}
              alt="user"
            />
            <span className="emp-name-text">{item.name || "N/A"}</span>
          </div>
        ),
        job_position: item.job_position?.job_title || "N/A",
        contact_info: item.phone_number || "N/A",
        user_id: item.employee_code || "N/A",
        Status: (
          <div className={`status-pill ${item.status ? 'status-active-pill' : 'status-inactive-pill'}`}>
            <span className="status-spot"></span>
            {item.status ? "Active" : "Inactive"}
          </div>
        ),
        actions: (
          <div className="d-flex align-items-center">
            {permissions.includes("editemployeeID") && (
              <span className="action-icon edit-icon" title="Edit" onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/editEmp/${item.id}`);
              }}>
                <IconPencilMinus size={18} stroke={2} />
              </span>
            )}
            {permissions.includes("deleteemployeeID") && (
              <span className="action-icon delete-icon" title="Delete" onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(item);
              }}>
                <IconTrash size={18} stroke={2} />
              </span>
            )}
          </div>
        )
      }));

      setData(transformedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDepartments();
  }, []);

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(
        `${process.env.REACT_APP_API}/api/account/users/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchData();
      handleCloseModal();
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  const handleRowClick = permissions.includes("employeedetailsID")
    ? (item) => navigate(`/user/empdetails/${item.id}`)
    : undefined;

  const handleSearch = useCallback(
    _.debounce((query) => {
      setLoading(true);
      fetchData(
        query,
        selectedEmployee.id,
        selectedType === "Active" ? "true" : selectedType === "Non-Active" ? "false" : ""
      );
    }, 1000),
    [selectedEmployee, selectedType]
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  const handleSelectEmployee = (departmentName, departmentId) => {
    setSelectedEmployee({ name: departmentName, id: departmentId });
    setLoading(true);
    fetchData(searchQuery, departmentId, selectedType === "Active" ? "true" : selectedType === "Non-Active" ? "false" : "");
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setLoading(true);
    fetchData(searchQuery, selectedEmployee.id, type === "Active" ? "true" : type === "Non-Active" ? "false" : "");
  };

  const handleSelectemployeeType = (type) => {
    setSelectedemployeeType(type);
    setLoading(true);
    const apiType = type === "All Employees" ? "" : type;
    fetchData(searchQuery, selectedEmployee.id, selectedType === "Active" ? "true" : selectedType === "Non-Active" ? "false" : "", apiType);
  };

  const TableSkeletonLoading = () => (
    <div className="w-100 py-5 text-center">
      <Spinner2 />
    </div>
  );

  const NoEmployeesMessage = () => (
    <div className="w-100 py-5 text-center text-muted">
      <img src="/assets/file.png" alt="No data" style={{ width: "80px", opacity: 0.2, marginBottom: "16px" }} />
      <p className="fw-semibold">No employees found matching your filters.</p>
    </div>
  );

  return (
    <div className="EmployeeContainer w-100 h-100 px-4 pt-4 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="emp_Header">
          <h4 className="m-0">Employee Directory</h4>
          <p className="text-muted small mb-0">Manage and view all personnel records across departments.</p>
        </div>

        {permissions.includes("addemployeeID") && (
          <Link to="/user/addemp" className="text-decoration-none">
            <button className="add-emp-btn">
              <i className="lni lni-plus me-2"></i> Add New Employee
            </button>
          </Link>
        )}
      </div>

      <div className="tableMainContainer">
        <div className="filters d-flex justify-content-between align-items-center pb-4 flex-wrap gap-3">
          <div className="d-flex gap-2 flex-wrap">
            <div className="dropdown">
              <button className="btn filter-btn dropdown-toggle" type="button" data-bs-toggle="dropdown">
                {selectedEmployee.name}
              </button>
              <ul className="dropdown-menu">
                <li><p className="dropdown-item" onClick={() => handleSelectEmployee("All Departments", "")}>All Departments</p></li>
                {departments.map((dept) => (
                  <li key={dept.id}><p className="dropdown-item" onClick={() => handleSelectEmployee(dept.dep_name, dept.id)}>{dept.dep_name}</p></li>
                ))}
              </ul>
            </div>

            <div className="dropdown">
              <button className="btn filter-btn dropdown-toggle" type="button" data-bs-toggle="dropdown">
                {selectedType}
              </button>
              <ul className="dropdown-menu">
                <li><p className="dropdown-item" onClick={() => handleSelectType("All Types")}>All Status</p></li>
                <li><p className="dropdown-item" onClick={() => handleSelectType("Active")}>Active Only</p></li>
                <li><p className="dropdown-item" onClick={() => handleSelectType("Non-Active")}>Inactive Only</p></li>
              </ul>
            </div>

            <div className="dropdown">
              <button className="btn filter-btn dropdown-toggle" type="button" data-bs-toggle="dropdown">
                {selectedemployeeType}
              </button>
              <ul className="dropdown-menu">
                <li><p className="dropdown-item" onClick={() => handleSelectemployeeType("All Employees")}>All Types</p></li>
                <li><p className="dropdown-item" onClick={() => handleSelectemployeeType("Employee")}>Full-time</p></li>
                <li><p className="dropdown-item" onClick={() => handleSelectemployeeType("Intern")}>Intern</p></li>
                <li><p className="dropdown-item" onClick={() => handleSelectemployeeType("Student")}>Student</p></li>
              </ul>
            </div>
          </div>

          <div className="Tablesearch position-relative">
            <input
              className="form-control"
              type="search"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: '280px' }}
            />
            <span style={{ position: "absolute", right: "15px", top: '50%', transform: 'translateY(-50%)' }}>
              <i className="bi bi-search"></i>
            </span>
          </div>
        </div>

        {loading ? (
          <TableSkeletonLoading />
        ) : product.length > 0 ? (
          <Table
            header={header}
            product={product}
            onRowClick={handleRowClick}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        ) : (
          <NoEmployeesMessage />
        )}
      </div>

      {permissions.includes("deleteemployeeID") && (
        <Modal open={openModal} onClose={handleCloseModal}>
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "white",
            boxShadow: 24,
            p: 4,
            borderRadius: '20px',
            outline: 'none',
            textAlign: 'center'
          }}>
            <div className="mb-4">
              <div className="d-inline-flex p-3 rounded-circle bg-danger bg-opacity-10 mb-3">
                <WarningAmberIcon sx={{ color: "#DC2626", fontSize: 48 }} />
              </div>
              <h5 className="fw-bold">Delete Employee?</h5>
              <p className="text-muted small">Are you sure you want to remove <b>{empToDelete?.name}</b> from the system? This action cannot be undone.</p>
            </div>
            <div className="d-flex gap-3">
              <button className="btn btn-light w-100 py-2 fw-semibold" onClick={handleCloseModal}>Cancel</button>
              <button className="btn btn-danger w-100 py-2 fw-semibold" onClick={() => handleDelete(empToDelete?.id)}>Confirm Delete</button>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
}